import React from "react";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Breadcrumbs } from "@src/components/Breadcrumbs";
import ClientCheckinsOverview from "@src/components/ClientCheckinsOverview";
import ClientDetails from "@src/components/ClientDetails";
import ClientDossierNote from "@src/components/ClientDossierNote";
import ClientEmotionsOverview from "@src/components/ClientEmotionsOverview";
import ClientQuestionnaireOverview from "@src/components/ClientQuestionnaireOverview";
import PageTitle from "@src/components/PageTitle";
import { useClientQuery } from "@src/queries/clients";

import authenticationService from "@src/services/authentication.service";
import ClientSessionsSection from "@src/views/ClientPage/ClientSessionsSection";
import EmployerReferralToolkitBanner from "@src/views/employer/EmployerReferralToolkitBanner";
import ClientAvailableSessionTypes from "@src/components/ClientAvailableSessionTypes";
import ClientMedicalCheckinsOverview from "@src/components/ClientMedicalCheckinsOverview";

const ClientDetailView: React.FC = () => {
  const { clientId } = useParams();
  const { t } = useTranslation();

  const { isManagerOrAdmin } = authenticationService;
  const { data: client } = useClientQuery(clientId);
  const hasFullAccess =
    isManagerOrAdmin() || client?.isInRelationWithCurrentPsychologist === true;

  return (
    <>
      <PageTitle title="Client" />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Breadcrumbs
            crumbs={[{ link: "/clients", text: t("MyClients.Pagetitle") }]}
          />
        </Grid>
        <Grid item xs={9}>
          <Grid container spacing={2}>
            {client?.employerId && !client?.isPartner && (
              <Grid item xs={12}>
                <EmployerReferralToolkitBanner employerId={client.employerId} />
              </Grid>
            )}
            <Grid item xs={12}>
              <ClientSessionsSection
                clientId={clientId}
                clientName={client?.firstName}
                canAddSession={isManagerOrAdmin()}
                viewNotesColumn={hasFullAccess}
                canJoinSession={hasFullAccess}
              />
            </Grid>
            {hasFullAccess && clientId && (
              <>
                <Grid item xs={12}>
                  <ClientEmotionsOverview clientId={clientId} />
                </Grid>
                <Grid item xs={12}>
                  <ClientCheckinsOverview clientId={clientId} />
                </Grid>
                <Grid item xs={12}>
                  <ClientMedicalCheckinsOverview clientId={clientId} />
                </Grid>
                <Grid item xs={12}>
                  <ClientQuestionnaireOverview clientId={clientId} />
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid item xs={3}>
          <ClientDetails clientId={clientId} canEditClient={hasFullAccess} />
          {hasFullAccess && clientId && (
            <Grid
              item
              xs={12}
              sx={{ marginTop: "24px" }}
              data-cy="client-notes-section"
            >
              <ClientAvailableSessionTypes clientId={clientId} />
              <ClientDossierNote clientId={clientId} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ClientDetailView;
